/**
 * Soft (advisory) ARAM perf budgets — SPY-08 / SPY-101 / SPY-116 / SPY-201.
 * Not a hard CI gate; optional CLI assert uses tickP95MsAssertCeiling only.
 */

export const ARAM_SOFT_PERF_BUDGET = Object.freeze({
  mapId: 'aram_proving_grounds',
  live: Object.freeze({
    /** Preferred mean FPS during a short `__debugPerf` sample. */
    targetFps: 55,
    /** Soft warn if mean FPS drops below this. */
    warnBelowFps: 45,
    /** Soft warn if frame p95 exceeds (~45 FPS). */
    frameP95MsSoftCap: 22,
    /** Investigate if frame p95 exceeds (~30 FPS). */
    frameP95MsInvestigate: 33,
    sampleSeconds: 3
  }),
  headlessTick: Object.freeze({
    /** Soft comfort under stress (60 minions / 30 projectiles). */
    tickP95MsSoftCap: 8,
    /** `pnpm perf:sim:assert` ceiling — catches pathological regressions. */
    tickP95MsAssertCeiling: 50,
    stressMinions: 60,
    stressProjectiles: 30,
    ticks: 120,
    dt: 0.016
  }),
  /**
   * Headless MP soak (`pnpm perf:soak`) — light 2-player load.
   * Soft fail / report only; CI gate stays optional (`--assert-soft`).
   */
  soak: Object.freeze({
    tickP95MsSoftCap: 16,
    warnBelowEffectiveFps: 45,
    tickP95MsAssertCeiling: 50
  }),
  /** Per-phase soft caps (ms p95) while `__debugPerf` is recording. */
  phases: Object.freeze({
    softCapMs: Object.freeze({
      buffGold: 1.5,
      minionSpawn: 1.0,
      championEntities: 2.0,
      minionAi: 3.0,
      championAttacks: 1.5,
      projectiles: 2.0,
      turretAttacks: 1.5,
      persistentZones: 1.0
    })
  }),
  /**
   * Alloc / GC soft sample via `__debugPerf` heap deltas (SPY-201).
   * Optional — skipped when `performance.memory` is unavailable.
   */
  alloc: Object.freeze({
    /** Soft warn if end−start heap grows beyond this (MB). */
    warnDeltaMb: 8,
    /** Investigate if heap delta exceeds this (MB). */
    investigateDeltaMb: 24
  })
});

export function evaluateLiveFramesAgainstSoftBudget(
  frames,
  budget = ARAM_SOFT_PERF_BUDGET.live
) {
  const warnings = [];
  const fps = Number(frames?.fps) || 0;
  const p95 = Number(frames?.p95Ms) || 0;
  if (fps < budget.warnBelowFps) {
    warnings.push(`fps ${fps.toFixed(1)} < warnBelowFps ${budget.warnBelowFps}`);
  }
  if (p95 > budget.frameP95MsInvestigate) {
    warnings.push(
      `frameP95 ${p95.toFixed(2)}ms > investigate ${budget.frameP95MsInvestigate}ms`
    );
  } else if (p95 > budget.frameP95MsSoftCap) {
    warnings.push(
      `frameP95 ${p95.toFixed(2)}ms > softCap ${budget.frameP95MsSoftCap}ms`
    );
  }
  return { ok: warnings.length === 0, warnings, metrics: { fps, p95Ms: p95 } };
}

export function evaluateTickMsAgainstSoftBudget(
  tickMs,
  budget = ARAM_SOFT_PERF_BUDGET.headlessTick
) {
  const warnings = [];
  const p95 = Number(tickMs?.p95Ms) || 0;
  if (p95 >= budget.tickP95MsAssertCeiling) {
    warnings.push(
      `tickP95 ${p95.toFixed(2)}ms >= assertCeiling ${budget.tickP95MsAssertCeiling}ms`
    );
  } else if (p95 > budget.tickP95MsSoftCap) {
    warnings.push(
      `tickP95 ${p95.toFixed(2)}ms > softCap ${budget.tickP95MsSoftCap}ms`
    );
  }
  return { ok: warnings.length === 0, warnings, metrics: { p95Ms: p95 } };
}

/**
 * Soft FPS/tick evaluation for MP soak samples (SPY-116).
 * softFail=true when soft caps warn but assert ceiling not breached.
 * @param {{ fps?: number, p95Ms?: number }} tickMs
 * @param {typeof ARAM_SOFT_PERF_BUDGET.soak} [budget]
 */
export function evaluateSoakAgainstSoftBudget(
  tickMs,
  budget = ARAM_SOFT_PERF_BUDGET.soak
) {
  const warnings = [];
  const fps = Number(tickMs?.fps) || 0;
  const p95 = Number(tickMs?.p95Ms) || 0;
  let softFail = false;
  if (fps < budget.warnBelowEffectiveFps) {
    warnings.push(
      `effectiveFps ${fps.toFixed(1)} < warnBelowEffectiveFps ${budget.warnBelowEffectiveFps}`
    );
    softFail = true;
  }
  if (p95 >= budget.tickP95MsAssertCeiling) {
    warnings.push(
      `tickP95 ${p95.toFixed(2)}ms >= assertCeiling ${budget.tickP95MsAssertCeiling}ms`
    );
    softFail = false;
  } else if (p95 > budget.tickP95MsSoftCap) {
    warnings.push(
      `tickP95 ${p95.toFixed(2)}ms > softCap ${budget.tickP95MsSoftCap}ms`
    );
    softFail = true;
  }
  return {
    ok: warnings.length === 0,
    softFail,
    warnings,
    metrics: { fps, p95Ms: p95 }
  };
}

/**
 * @param {Record<string, { p95Ms?: number }>} phaseSummaries
 * @param {Record<string, number>} [caps]
 */
export function evaluatePhaseTimingsAgainstSoftBudget(
  phaseSummaries,
  caps = ARAM_SOFT_PERF_BUDGET.phases.softCapMs
) {
  const warnings = [];
  const metrics = {};
  const summaries = phaseSummaries && typeof phaseSummaries === 'object'
    ? phaseSummaries
    : {};
  for (const [id, softCap] of Object.entries(caps || {})) {
    const p95 = Number(summaries[id]?.p95Ms) || 0;
    metrics[id] = { p95Ms: p95, softCapMs: softCap };
    if (p95 > softCap) {
      warnings.push(`${id} p95 ${p95.toFixed(2)}ms > softCap ${softCap}ms`);
    }
  }
  return { ok: warnings.length === 0, warnings, metrics };
}

/**
 * Soft alloc/GC evaluation from `__debugPerf` heap samples (SPY-201).
 * Skips (ok) when heap API unavailable so CI stays deterministic.
 * @param {{ startHeapMb?: number|null, endHeapMb?: number|null, peakHeapMb?: number|null, sampleCount?: number }} sample
 * @param {typeof ARAM_SOFT_PERF_BUDGET.alloc} [budget]
 */
export function evaluateAllocAgainstSoftBudget(
  sample,
  budget = ARAM_SOFT_PERF_BUDGET.alloc
) {
  const start = Number(sample?.startHeapMb);
  const end = Number(sample?.endHeapMb);
  const peak = Number(sample?.peakHeapMb);
  const sampleCount = Number(sample?.sampleCount) || 0;
  if (!(start >= 0) || !(end >= 0) || sampleCount <= 0) {
    return {
      ok: true,
      skipped: true,
      warnings: [],
      metrics: {
        startHeapMb: null,
        endHeapMb: null,
        peakHeapMb: null,
        deltaMb: null,
        sampleCount
      }
    };
  }
  const deltaMb = end - start;
  const warnings = [];
  if (deltaMb > budget.investigateDeltaMb) {
    warnings.push(
      `deltaMb ${deltaMb.toFixed(2)} > investigate ${budget.investigateDeltaMb}MB`
    );
  } else if (deltaMb > budget.warnDeltaMb) {
    warnings.push(
      `deltaMb ${deltaMb.toFixed(2)} > warnDeltaMb ${budget.warnDeltaMb}MB`
    );
  }
  return {
    ok: warnings.length === 0,
    skipped: false,
    warnings,
    metrics: {
      startHeapMb: start,
      endHeapMb: end,
      peakHeapMb: peak >= 0 ? peak : end,
      deltaMb,
      sampleCount
    }
  };
}
